import { useEffect, useState } from 'react';

/**
 * A drop-in `useState` replacement that transparently persists its value to
 * sessionStorage. This means an in-progress value survives the browser
 * discarding and restoring the tab — which happens routinely on mobile when
 * the user switches apps/tabs (e.g. to copy a price or bank detail) and comes
 * back. sessionStorage is:
 *   - per-tab (concurrent drafts in other tabs don't collide)
 *   - kept through tab discard/restore within the same session
 *   - cleared when the tab is finally closed
 *
 * Returns the standard `[value, setValue]` plus a `clear()` helper to drop the
 * persisted entry after a successful submit/cancel so it isn't restored later.
 */
export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [key, value]);

  const clear = () => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  };

  return [value, setValue, clear] as const;
}
